/**
 * API helpers for betting game Edge Functions
 */

import { supabase } from './supabase';
import { calculateWinLoss } from '../utils/betting';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;

/**
 * Place a bet on a game
 * @param {string} gameId - Game ID
 * @param {number} teamNumber - Team number (1-7)
 * @param {number} amount - Bet amount
 * @returns {Promise<Object>} - Response with bet details
 */
export async function placeBet(gameId, teamNumber, amount) {
  try {
    // Get current user (validates session with server)
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    
    if (userError || !user) {
      throw new Error('Not authenticated');
    }
    
    // Get session after validating user
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError || !session) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(
      `${SUPABASE_URL}/functions/v1/place-bet`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          game_id: gameId,
          team_number: teamNumber,
          amount: amount
        })
      }
    );

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to place bet');
    }

    return data;
  } catch (error) {
    console.error('Error placing bet:', error);
    throw error;
  }
}

/**
 * Get games list
 * @param {string} status - 'active', 'ended', 'cancelled', or 'all'
 * @param {number} limit - Number of games to return
 * @param {number} offset - Offset for pagination
 * @returns {Promise<Object>} - Response with games array
 */
export async function getGames(status = 'active', limit = 100, offset = 0) {
  try {
    // Try Edge Function first
    if (SUPABASE_URL) {
      try {
        const response = await fetch(
          `${SUPABASE_URL}/functions/v1/get-games?status=${status}&limit=${limit}&offset=${offset}`
        );

        const data = await response.json();

        if (response.ok && data.games) {
          // Transform games to maintain backward compatibility with yes/no format
          const gamesWithCompatibility = (data.games || []).map(game => {
            // For 2-team games, map team 1 to yes, team 2 to no
            const team1 = game.teams && game.teams.find(t => t.team_number === 1);
            const team2 = game.teams && game.teams.find(t => t.team_number === 2);
            
            const poolYes = team1?.pool || 0;
            const poolNo = team2?.pool || 0;
            
            return {
              ...game,
              // Backward compatibility fields
              pool_yes: poolYes,
              pool_no: poolNo,
              team_yes_name: team1?.name || null,
              team_yes_image: team1?.image || null,
              team_yes_color: team1?.color || null,
              team_no_name: team2?.name || null,
              team_no_image: team2?.image || null,
              team_no_color: team2?.color || null,
              coefficient_yes: team1?.coefficient || 1,
              coefficient_no: team2?.coefficient || 1
            };
          });

          return { games: gamesWithCompatibility };
        }
        // If edge function fails, fall back to direct query
        console.log('Edge Function failed, using direct query');
      } catch (edgeError) {
        console.log('Edge Function not available, using direct query:', edgeError);
      }
    }

    // Fallback: Use direct Supabase query
    let query = supabase
      .from('games')
      .select('*')
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1);

    if (status !== 'all') {
      query = query.eq('status', status);
    }

    const { data: games, error } = await query;

    if (error) {
      throw error;
    }

    // Calculate coefficients for each game and build teams array
    const gamesWithCoefficients = (games || []).map(game => {
      // Build teams array from pool_1..pool_7
      const teams = [];
      for (let i = 1; i <= 7; i++) {
        const pool = parseFloat(game[`pool_${i}`]) || 0;
        const name = game[`team_${i}_name`];
        if (name || pool > 0) {
          const totalPool = Array.from({ length: 7 }, (_, j) => parseFloat(game[`pool_${j + 1}`]) || 0).reduce((a, b) => a + b, 0);
          const defaultBet = 1;
          const coefficient = totalPool > 0 ? (totalPool + defaultBet) / (pool + defaultBet) : 1;
          
          teams.push({
            team_number: i,
            name: name,
            image: game[`team_${i}_image`] || null,
            color: game[`team_${i}_color`] || null,
            pool: pool,
            coefficient: parseFloat(coefficient.toFixed(4))
          });
        }
      }

      // For backward compatibility, map team 1 to yes, team 2 to no
      const team1 = teams.find(t => t.team_number === 1);
      const team2 = teams.find(t => t.team_number === 2);
      const poolYes = team1?.pool || parseFloat(game.pool_yes) || 0;
      const poolNo = team2?.pool || parseFloat(game.pool_no) || 0;
      const totalPool = poolYes + poolNo;
      const defaultBet = 1;
      const coefYes = totalPool > 0 ? (totalPool + defaultBet) / (poolYes + defaultBet) : 1;
      const coefNo = totalPool > 0 ? (totalPool + defaultBet) / (poolNo + defaultBet) : 1;

      return {
        ...game,
        teams: teams,
        pool_yes: poolYes,
        pool_no: poolNo,
        team_yes_name: team1?.name || game.team_yes_name || null,
        team_yes_image: team1?.image || game.team_yes_image || null,
        team_yes_color: team1?.color || game.team_yes_color || null,
        team_no_name: team2?.name || game.team_no_name || null,
        team_no_image: team2?.image || game.team_no_image || null,
        team_no_color: team2?.color || game.team_no_color || null,
        coefficient_yes: parseFloat(coefYes.toFixed(4)),
        coefficient_no: parseFloat(coefNo.toFixed(4))
      };
    });

    return { games: gamesWithCoefficients };
  } catch (error) {
    console.error('Error fetching games:', error);
    throw error;
  }
}

/**
 * Get a single game by ID
 * @param {string} gameId - Game ID
 * @returns {Promise<Object>} - Game object
 */
export async function getGame(gameId) {
  try {
    // Try Edge Function first
    if (SUPABASE_URL) {
      try {
        const response = await fetch(
          `${SUPABASE_URL}/functions/v1/get-games?status=all&limit=1000&offset=0`
        );

        const data = await response.json();

        if (response.ok && data.games) {
          // Find the specific game
          const game = data.games?.find(g => g.id === gameId);
          
          if (game) {
            // Transform game to maintain backward compatibility
            const team1 = game.teams && game.teams.find(t => t.team_number === 1);
            const team2 = game.teams && game.teams.find(t => t.team_number === 2);
            
            return {
              ...game,
              // Backward compatibility fields
              pool_yes: team1?.pool || 0,
              pool_no: team2?.pool || 0,
              team_yes_name: team1?.name || null,
              team_yes_image: team1?.image || null,
              team_yes_color: team1?.color || null,
              team_no_name: team2?.name || null,
              team_no_image: team2?.image || null,
              team_no_color: team2?.color || null,
              coefficient_yes: team1?.coefficient || 1,
              coefficient_no: team2?.coefficient || 1
            };
          }
        }
        // If edge function fails or game not found, fall back to direct query
        console.log('Edge Function failed or game not found, using direct query');
      } catch (edgeError) {
        console.log('Edge Function not available, using direct query:', edgeError);
      }
    }

    // Fallback: Use direct Supabase query
    const { data: directData, error } = await supabase
      .from('games')
      .select('*')
      .eq('id', gameId)
      .single();

    if (error) {
      throw error;
    }

    // Build teams array from pool_1..pool_7
    const teams = [];
    for (let i = 1; i <= 7; i++) {
      const pool = parseFloat(directData[`pool_${i}`]) || 0;
      const name = directData[`team_${i}_name`];
      if (name || pool > 0) {
        const totalPool = Array.from({ length: 7 }, (_, j) => parseFloat(directData[`pool_${j + 1}`]) || 0).reduce((a, b) => a + b, 0);
        const defaultBet = 1;
        const coefficient = totalPool > 0 ? (totalPool + defaultBet) / (pool + defaultBet) : 1;
        
        teams.push({
          team_number: i,
          name: name,
          image: directData[`team_${i}_image`] || null,
          color: directData[`team_${i}_color`] || null,
          pool: pool,
          coefficient: parseFloat(coefficient.toFixed(4))
        });
      }
    }

    const team1 = teams.find(t => t.team_number === 1);
    const team2 = teams.find(t => t.team_number === 2);
    
    return {
      ...directData,
      teams: teams,
      pool_yes: team1?.pool || parseFloat(directData.pool_yes) || 0,
      pool_no: team2?.pool || parseFloat(directData.pool_no) || 0,
      team_yes_name: team1?.name || directData.team_yes_name || null,
      team_yes_image: team1?.image || directData.team_yes_image || null,
      team_yes_color: team1?.color || directData.team_yes_color || null,
      team_no_name: team2?.name || directData.team_no_name || null,
      team_no_image: team2?.image || directData.team_no_image || null,
      team_no_color: team2?.color || directData.team_no_color || null,
      coefficient_yes: team1?.coefficient || 1,
      coefficient_no: team2?.coefficient || 1
    };
  } catch (error) {
    console.error('Error fetching game:', error);
    throw error;
  }
}

/**
 * Create a new game
 * @param {Object} gameData - { title, description?, ends_at?, title_image?, team_yes_name?, team_yes_image?, team_yes_color?, team_no_name?, team_no_image?, team_no_color? }
 * @param {string} gameData.ends_at - ISO timestamp string (calculated from duration: 1h, 24h, or 7d)
 * @returns {Promise<Object>} - Created game
 */
export async function createGame(gameData) {
  try {
    // Get current user (validates session with server)
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    
    if (userError || !user) {
      throw new Error('Not authenticated');
    }
    
    // Get session after validating user
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError || !session) {
      throw new Error('Not authenticated');
    }

    // Build teams array for Edge Function
    let teams = [];
    
    // If teams array is already provided, use it
    if (Array.isArray(gameData.teams)) {
      teams = gameData.teams;
    } else {
      // Otherwise, build from old format for backward compatibility
      // Team 1 (old "yes")
      if (gameData.team_yes_name) {
        teams.push({
          name: gameData.team_yes_name,
          image: gameData.team_yes_image || null,
          color: gameData.team_yes_color || '#2551AF'
        });
      }
      
      // Team 2 (old "no")
      if (gameData.team_no_name) {
        teams.push({
          name: gameData.team_no_name,
          image: gameData.team_no_image || null,
          color: gameData.team_no_color || '#E23838'
        });
      }
    }

    // Validate we have at least 2 teams
    if (teams.length < 2) {
      throw new Error('At least 2 teams are required');
    }

    // Try Edge Function first
    try {
      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/create-game`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            title: gameData.title,
            description: gameData.description || null,
            ends_at: gameData.ends_at || null,
            title_image: gameData.title_image || null,
            teams: teams
          })
        }
      );

      const data = await response.json();

      if (response.ok) {
        // Transform response to maintain backward compatibility
        const team1 = data.game?.teams?.find(t => t.team_number === 1) || 
                     (data.game && { 
                       team_number: 1, 
                       name: data.game.team_1_name, 
                       image: data.game.team_1_image, 
                       color: data.game.team_1_color,
                       pool: data.game.pool_1 || 0
                     });
        const team2 = data.game?.teams?.find(t => t.team_number === 2) || 
                     (data.game && { 
                       team_number: 2, 
                       name: data.game.team_2_name, 
                       image: data.game.team_2_image, 
                       color: data.game.team_2_color,
                       pool: data.game.pool_2 || 0
                     });
        
        return {
          ...data,
          game: {
            ...data.game,
            pool_yes: team1?.pool || 0,
            pool_no: team2?.pool || 0,
            team_yes_name: team1?.name || null,
            team_yes_image: team1?.image || null,
            team_yes_color: team1?.color || null,
            team_no_name: team2?.name || null,
            team_no_image: team2?.image || null,
            team_no_color: team2?.color || null
          }
        };
      }
      // If Edge Function fails, throw error
      throw new Error(data.error || 'Failed to create game');
    } catch (edgeError) {
      console.error('Edge Function error:', edgeError);
      throw edgeError;
    }
  } catch (error) {
    console.error('Error creating game:', error);
    throw error;
  }
}

/**
 * Get user's bets for a specific game
 * @param {string} gameId - Game ID
 * @returns {Promise<Array>} - Array of bets
 */
export async function getUserBets(gameId) {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      return [];
    }

    const { data, error } = await supabase
      .from('bets')
      .select('*')
      .eq('game_id', gameId)
      .eq('user_id', session.user.id)
      .order('created_at', { ascending: false });

    if (error) {
      throw error;
    }

    // Convert old format (side) to new format (team_number) for backward compatibility
    const bets = (data || []).map(bet => {
      if (bet.team_number === undefined || bet.team_number === null) {
        // Old format: convert side to team_number
        bet.team_number = bet.side === 'yes' ? 1 : bet.side === 'no' ? 2 : null;
      }
      return bet;
    });

    return bets;
  } catch (error) {
    console.error('Error fetching user bets:', error);
    throw error;
  }
}

/**
 * Get bet statistics for a game (counts and totals for each side)
 * @param {string} gameId - Game ID
 * @returns {Promise<Object>} - { yes: { count, total }, no: { count, total } }
 */
export async function getGameBetStats(gameId) {
  try {
    // Get all bets for the game (we need all, not just recent ones)
    const { data: bets, error: betsError } = await supabase
      .from('bets')
      .select('side, team_number, amount')
      .eq('game_id', gameId);

    if (betsError) {
      throw betsError;
    }

    // Calculate statistics
    // Support both old format (side) and new format (team_number)
    // Old bets: side='yes' → team_number=1, side='no' → team_number=2
    const stats = {
      yes: { count: 0, total: 0 },
      no: { count: 0, total: 0 },
      teams: {} // per-team stats by team_number for multi-team games
    };

    if (bets && bets.length > 0) {
      bets.forEach(bet => {
        // Determine team_number: use team_number if available, otherwise convert from side
        let teamNum = bet.team_number;
        if (teamNum === undefined || teamNum === null) {
          // Old format: convert side to team_number
          teamNum = bet.side === 'yes' ? 1 : bet.side === 'no' ? 2 : null;
        }
        
        // Team 1 = yes, Team 2 = no
        if (teamNum === 1) {
          stats.yes.count++;
          stats.yes.total += parseFloat(bet.amount) || 0;
        } else if (teamNum === 2) {
          stats.no.count++;
          stats.no.total += parseFloat(bet.amount) || 0;
        }

        // Track per-team stats as well (1–7)
        if (teamNum != null) {
          if (!stats.teams[teamNum]) {
            stats.teams[teamNum] = { count: 0, total: 0 };
          }
          stats.teams[teamNum].count++;
          stats.teams[teamNum].total += parseFloat(bet.amount) || 0;
        }
      });
    }

    return stats;
  } catch (error) {
    console.error('Error fetching bet statistics:', error);
    throw error;
  }
}

/**
 * Get recent bets for a game (for display)
 * @param {string} gameId - Game ID
 * @param {number} limit - Number of bets to return
 * @returns {Promise<Array>} - Array of bets with user info
 */
/**
 * Get complete betting history for a user
 * Returns all ended games with bets, including win/loss calculations
 * @param {string} userId - User ID
 * @returns {Promise<Array>} - Array of history items with game, bets, and win/loss
 */
export async function getUserBettingHistory(userId) {
  try {
    // 1. Get all bets for the user
    const { data: bets, error: betsError } = await supabase
      .from('bets')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (betsError) {
      throw betsError;
    }

    if (!bets || bets.length === 0) {
      return [];
    }

    // Convert old format (side) to new format (team_number) for backward compatibility
    const betsWithTeamNumber = bets.map(bet => {
      if (bet.team_number === undefined || bet.team_number === null) {
        // Old format: convert side to team_number
        bet.team_number = bet.side === 'yes' ? 1 : bet.side === 'no' ? 2 : null;
      }
      return bet;
    });

    // 2. Get unique game IDs
    const gameIds = [...new Set(betsWithTeamNumber.map(bet => bet.game_id))];

    // 3. Fetch game details (only ended games)
    const { data: games, error: gamesError } = await supabase
      .from('games')
      .select('*')
      .in('id', gameIds)
      .eq('status', 'ended')
      .order('ends_at', { ascending: false });

    if (gamesError) {
      throw gamesError;
    }

    if (!games || games.length === 0) {
      return [];
    }

    // 4. Group bets by game and calculate win/loss
    const history = games.map(game => {
      const gameBets = betsWithTeamNumber.filter(bet => bet.game_id === game.id);
      
      // Only calculate win/loss if game has a winner
      let winLoss = null;
      if (game.winner_side) {
        try {
          winLoss = calculateWinLoss(gameBets, game.winner_side);
        } catch (error) {
          console.error('Error calculating win/loss for game:', game.id, error);
          winLoss = {
            won: false,
            payoutDifference: 0,
            winningBetsCount: 0,
            losingBetsCount: gameBets.length
          };
        }
      } else {
        // Game ended but no winner determined
        winLoss = {
          won: false,
          payoutDifference: 0,
          winningBetsCount: 0,
          losingBetsCount: gameBets.length
        };
      }
      
      return {
        game: game,
        bets: gameBets,
        ...winLoss, // won, payoutDifference, winningBetsCount, losingBetsCount
        endedAt: game.ends_at
      };
    });

    return history;
  } catch (error) {
    console.error('Error fetching betting history:', error);
    throw error;
  }
}

/**
 * Claim daily bonus ($10)
 * @returns {Promise<Object>} - { success, bonusAmount, newBalance, nextClaimTime, error, hoursRemaining }
 */
export async function claimDailyBonus() {
  try {
    // Get current session
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError || !session) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(
      `${SUPABASE_URL}/functions/v1/claim-daily-bonus`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        }
      }
    );

    const result = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: result.error || 'Failed to claim daily bonus',
        nextClaimTime: result.nextClaimTime ? new Date(result.nextClaimTime) : null,
        hoursRemaining: result.hoursRemaining
      };
    }

    return {
      success: true,
      bonusAmount: result.bonusAmount,
      newBalance: result.newBalance,
      nextClaimTime: result.nextClaimTime ? new Date(result.nextClaimTime) : null,
      wallet: result.wallet
    };
  } catch (error) {
    console.error('Error claiming daily bonus:', error);
    return {
      success: false,
      error: error.message || 'Failed to claim daily bonus'
    };
  }
}

export async function getGameBets(gameId, limit = 20) {
  try {
    // First, get the bets
    // This should work for both authenticated and anonymous users
    // RLS policies should allow public read access to bets
    const { data: bets, error: betsError } = await supabase
      .from('bets')
      .select('*')
      .eq('game_id', gameId)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (betsError) {
      console.error('Error fetching bets:', betsError);
      // If RLS blocks the query, return empty array instead of throwing
      // This allows the UI to still render for anonymous users
      if (betsError.code === 'PGRST301' || betsError.message?.includes('permission') || betsError.message?.includes('policy')) {
        console.warn('Bets query blocked by RLS - returning empty array for anonymous user');
        return [];
      }
      throw betsError;
    }

    if (!bets || bets.length === 0) {
      return [];
    }

    // Convert old format (side) to new format (team_number) for backward compatibility
    const betsWithTeamNumber = bets.map(bet => {
      if (bet.team_number === undefined || bet.team_number === null) {
        // Old format: convert side to team_number
        bet.team_number = bet.side === 'yes' ? 1 : bet.side === 'no' ? 2 : null;
      }
      return bet;
    });

    // Get unique user IDs (filter out null/undefined)
    const userIds = [...new Set(betsWithTeamNumber.map(bet => bet.user_id).filter(id => id))];

    if (userIds.length === 0) {
      console.warn('No user IDs found in bets');
      return bets.map(bet => ({
        ...bet,
        user_profiles: {
          nickname: 'Anonymous'
        }
      }));
    }

    // Fetch user profiles for these users
    // Use user_profiles_public view to only access id and nickname (not email)
    const { data: profiles, error: profilesError } = await supabase
      .from('user_profiles_public')
      .select('id, nickname')
      .in('id', userIds);

    if (profilesError) {
      console.error('Error fetching user profiles:', profilesError);
    }

    // Create a map of user_id to nickname
    const profileMap = {};
    if (profiles && profiles.length > 0) {
      profiles.forEach(profile => {
        if (profile && profile.id && profile.nickname) {
          // Ensure we're using the exact ID format (string comparison)
          profileMap[profile.id] = profile.nickname;
        }
      });
    }

    // Find which user IDs don't have profiles
    const foundUserIds = profiles ? profiles.map(p => p.id) : [];
    const missingUserIds = userIds.filter(id => !foundUserIds.includes(id));

    // For users without profiles, try individual queries first
    // This handles cases where .in() might not work due to UUID format issues
    if (missingUserIds.length > 0) {
      for (const userId of missingUserIds) {
        // Try to query each missing user individually
        const { data: singleProfile, error: singleError } = await supabase
          .from('user_profiles_public')
          .select('id, nickname')
          .eq('id', userId)
          .maybeSingle(); // Use maybeSingle() instead of single() to avoid errors if not found

        if (singleError) {
          console.warn(`Error fetching profile for user ${userId}:`, singleError);
        } else if (singleProfile && singleProfile.nickname) {
          profileMap[userId] = singleProfile.nickname;
        } else {
          // Profile doesn't exist - generate a readable display name from user ID (first 8 chars)
          const shortId = userId.substring(0, 8);
          profileMap[userId] = `User ${shortId}`;
        }
      }
    }

    // Combine bets with nicknames
    const betsWithProfiles = betsWithTeamNumber.map(bet => {
      const nickname = profileMap[bet.user_id] || 'Anonymous';
      
      return {
        ...bet,
        user_profiles: {
          nickname: nickname
        }
      };
    });

    return betsWithProfiles;
  } catch (error) {
    console.error('Error fetching game bets:', error);
    throw error;
  }
}

